import { Form, Input, Modal, Row, Select, Upload, message, Button, Switch } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd";
import { get, post } from "../../../utils/http";
import React, { useEffect, useState } from "react";
import { PERMISSION_MAP, PERMISSIONS, STATUS } from "../../../utils/consts";

export default function CreateModal({
  data,
  show,
  closeModalCb,
}: {
  data: any;
  show: boolean;
  closeModalCb: () => void;
}) {
  const [form] = Form.useForm();
  const [mainPicFileList, setMainPicFileList] = useState<UploadFile[]>([]);
  const [dialogPicFileList, setDialogPicFileList] = useState<UploadFile[]>([]);
  const [moduleList, setModuleList] = useState<any[]>([]);
  const [newModuleName, setNewModuleName] = useState("");
  const [selectedModule, setSelectedModule] = useState<string>("");
  const [sceneNameEn, setSceneNameEn] = useState<string>("");
  const [isGeneratingTranslation, setIsGeneratingTranslation] =
    useState<boolean>(false);
  const [isRecommend, setIsRecommend] = useState<boolean>(false);

  const fetchModuleList = async () => {
    try {
      const response = await get("/api/manage/module-list");
      console.log("🚀 ~ fetchModuleList ~ response:", response);
      if (response?.data?.code === 0) {
        // 正确路径是data.list
        const moduleData = response.data.data.list || [];
        setModuleList(Array.isArray(moduleData) ? moduleData : []);
      }
    } catch (error) {
      console.error("🚀 ~ fetchModuleList ~ error:", error);
    }
  };

  const handleModuleCreate = async () => {
    if (!newModuleName.trim()) {
      message.warning("请输入模块名称");
      return;
    }
    try {
      const response = await post("/api/manage/module-create", {
        module_name: newModuleName,
      });
      console.log("🚀 ~ handleModuleCreate ~ response:", response);
      if (response?.data?.code === 0) {
        message.success("模块创建成功");
        const newModule = response.data.data;
        setNewModuleName("");
        await fetchModuleList();
        const newModuleValue = `${newModule.id}-${newModule.module_name}`;
        setSelectedModule(newModuleValue);
        form.setFieldValue("moduleInfo", newModuleValue);
      }
    } catch (error) {
      console.error("🚀 ~ handleModuleCreate ~ error:", error);
      message.error("模块创建失败");
    }
  };

  const customUpload = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await post("/api/manage/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress({ percent: percentCompleted });
        },
      });

      if (response.data && response.data.data.url) {
        onSuccess(response.data.data);
        message.success("上传成功");
      } else {
        throw new Error("上传失败");
      }
    } catch (error) {
      onError(error);
      message.error("上传失败");
    }
  };

  const handleOk = () => {
    // AI 生成过程中禁止保存
    if (isGeneratingTranslation) {
      message.warning("AI 正在生成翻译，请耐心等待完成后再保存");
      return;
    }

    const value = form.getFieldsValue();

    if (value.is_recommend) {
      if (!value.recommend_text_zh) {
        message.error("请输入推荐语-中文");
        return;
      }
      if (!value.recommend_text_en) {
        message.error("请输入推荐语-英文");
        return;
      }
    }
    console.log("🚀 ~ handleOk ~ value:", value);

    const apiUrl = data
      ? "/api/manage/scene-update"
      : "/api/manage/scene-create";

    let module_id = "";
    let module_name = "";

    if (value.moduleInfo) {
      const moduleInfo = value.moduleInfo.split("-");
      module_id = moduleInfo[0];
      module_name = moduleInfo[1] || "";
    }

    const requestData = data
      ? {
          ...value,
          id: data.id,
          module_id,
          module_name,
        }
      : {
          ...value,
          module_id,
          module_name,
        };

    post(apiUrl, requestData)
      .then((res: any) => {
        console.log("🚀 ~ post ~ res:", res);
        if (res?.data?.code === 0) {
          closeModalCb();
        }
      })
      .catch((err: any) => {
        console.log("🚀 ~ post ~ err:", err);
      });
  };
  const handleCancel = () => {
    // 关闭模态框时重置状态
    setSelectedModule("");
    setNewModuleName("");
    setSceneNameEn("");
    closeModalCb();
  };

  const handleMainPicChange: UploadProps["onChange"] = ({ fileList }) => {
    setMainPicFileList(fileList);
    if (fileList.length > 0 && fileList[0].status === "done") {
      form.setFieldValue(
        "main_pic",
        fileList[0].url || fileList[0].response?.url,
      );
    } else {
      form.setFieldValue("main_pic", "");
    }
  };

  const handleDialogPicChange: UploadProps["onChange"] = ({ fileList }) => {
    setDialogPicFileList(fileList);
    if (fileList.length > 0 && fileList[0].status === "done") {
      form.setFieldValue(
        "dialog_pic",
        fileList[0].url || fileList[0].response?.url,
      );
    } else {
      form.setFieldValue("dialog_pic", "");
    }
  };

  useEffect(() => {
    form.setFieldsValue(data);

    if (data?.main_pic) {
      setMainPicFileList([
        {
          uid: "-1",
          name: "封面图片",
          status: "done",
          url: data.main_pic,
        },
      ]);
    } else {
      setMainPicFileList([]);
    }

    if (data?.dialog_pic) {
      setDialogPicFileList([
        {
          uid: "-1",
          name: "对话背景图",
          status: "done",
          url: data.dialog_pic,
        },
      ]);
    } else {
      setDialogPicFileList([]);
    }

    if (data?.module_id && data?.module_name) {
      const moduleValue = `${data.module_id}-${data.module_name}`;
      setSelectedModule(moduleValue);
      form.setFieldValue("moduleInfo", moduleValue);
    } else {
      setSelectedModule("");
      form.setFieldValue("moduleInfo", "");
    }

    if (data?.scene_name_en) {
      setSceneNameEn(data.scene_name_en);
      form.setFieldValue("scene_name_en", data.scene_name_en);
    } else {
      setSceneNameEn("");
      form.setFieldValue("scene_name_en", "");
    }

    if (data?.is_recommend !== undefined) {
      setIsRecommend(!!data.is_recommend);
      form.setFieldValue("is_recommend", !!data.is_recommend);
    } else {
      setIsRecommend(false);
      form.setFieldValue("is_recommend", false);
    }
  }, [data, form]);

  useEffect(() => {
    if (show) {
      fetchModuleList();
      // 当显示模态框且是创建场景时，重置表单
      if (!data) {
        form.resetFields();
        form.setFieldsValue({
          permission_type: PERMISSIONS.FREE,
          scene_status: STATUS.DISABLE,
        });
        setMainPicFileList([]);
        setDialogPicFileList([]);
        setSceneNameEn("");
        setSelectedModule("");
        setIsRecommend(false);
      }
    }
  }, [show, data]);

  return (
    <Modal
      title={data ? "编辑场景" : "创建场景"}
      open={show}
      onOk={handleOk}
      onCancel={handleCancel}
    >
      <Form form={form} labelCol={{ span: 5 }} wrapperCol={{ span: 19 }}>
        <Form.Item label="场景名称" name="scene_name">
          <Input></Input>
        </Form.Item>
        <Form.Item label="场景名称翻译" name="scene_name_en">
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              placeholder="请输入场景名称的英文翻译"
              style={{ flex: 1 }}
              value={sceneNameEn}
              onChange={(e) => {
                setSceneNameEn(e.target.value);
                form.setFieldValue("scene_name_en", e.target.value);
              }}
              disabled={isGeneratingTranslation}
            ></Input>
            <Button
              type="default"
              loading={isGeneratingTranslation}
              onClick={async () => {
                const sceneName = form.getFieldValue("scene_name");
                if (!sceneName) {
                  message.warning("请先输入场景名称");
                  return;
                }
                try {
                  setIsGeneratingTranslation(true);
                  message.info("正在生成英文翻译，请耐心等待...");
                  const response = await post(
                    "/api/manage/generate-english-translation",
                    { text: sceneName },
                  );
                  if (
                    response?.data?.code === 0 &&
                    response?.data?.data?.translation
                  ) {
                    const translation = response.data.data.translation;
                    setSceneNameEn(translation);
                    form.setFieldValue("scene_name_en", translation);
                    message.success("翻译生成成功");
                  } else {
                    message.error("翻译生成失败");
                  }
                } catch (error) {
                  console.error("翻译生成失败:", error);
                  message.error("翻译生成失败");
                } finally {
                  setIsGeneratingTranslation(false);
                }
              }}
            >
              AI生成
            </Button>
          </div>
        </Form.Item>
        <Form.Item label="所属模块" name="moduleInfo">
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Select
              style={{ flex: 1 }}
              value={selectedModule}
              options={moduleList.map((module) => ({
                label: module.module_name,
                value: `${module.id}-${module.module_name}`,
              }))}
              onChange={(value) => {
                setSelectedModule(value);
                form.setFieldValue("moduleInfo", value);
              }}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
            <Input
              placeholder="新模块名称"
              value={newModuleName}
              onChange={(e) => setNewModuleName(e.target.value)}
              onPressEnter={handleModuleCreate}
              style={{ width: 150 }}
            />
            <Button type="primary" onClick={handleModuleCreate}>
              添加
            </Button>
          </div>
        </Form.Item>
        <Form.Item label="所属权限" name="permission_type">
          <Select
            options={[
              {
                value: PERMISSIONS.FREE,
                label: PERMISSION_MAP[PERMISSIONS.FREE],
              },
              {
                value: PERMISSIONS.NORMAL,
                label: PERMISSION_MAP[PERMISSIONS.NORMAL],
              },
              {
                label: PERMISSION_MAP[PERMISSIONS.VIP],
                value: PERMISSIONS.VIP,
              },
            ]}
          />
        </Form.Item>
        <Form.Item label="状态" name="scene_status">
          <Select
            options={[
              {
                label: "上架",
                value: STATUS.INUSE,
              },
              {
                label: "下架",
                value: STATUS.DISABLE,
              },
            ]}
          />
        </Form.Item>
        <Form.Item label="是否推荐" name="is_recommend" valuePropName="checked">
          <Switch
            checked={isRecommend}
            onChange={(checked) => {
              setIsRecommend(checked);
              form.setFieldValue("is_recommend", checked);
            }}
            checkedChildren="是"
            unCheckedChildren="否"
          />
        </Form.Item>
        {isRecommend && (
          <>
            <Form.Item label="推荐语-中文" name="recommend_text_zh">
              <Input placeholder="请输入推荐语（中文）" />
            </Form.Item>
            <Form.Item label="推荐语-英文" name="recommend_text_en">
              <Input placeholder="请输入推荐语（英文）" />
            </Form.Item>
          </>
        )}
        <Form.Item label="场景描述" name="scene_desc">
          <Input.TextArea rows={4}></Input.TextArea>
        </Form.Item>
        <Form.Item label="封面图片" name="main_pic">
          <Upload
            listType="picture-card"
            fileList={mainPicFileList}
            onChange={handleMainPicChange}
            customRequest={customUpload}
            maxCount={1}
          >
            {mainPicFileList.length === 0 && (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>上传</div>
              </div>
            )}
          </Upload>
        </Form.Item>
        <Form.Item label="对话背景图" name="dialog_pic">
          <Upload
            listType="picture-card"
            fileList={dialogPicFileList}
            onChange={handleDialogPicChange}
            customRequest={customUpload}
            maxCount={1}
          >
            {dialogPicFileList.length === 0 && (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>上传</div>
              </div>
            )}
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
}
